import { Alert, Space, Spoiler, Stack, Tabs, Text } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { useState } from 'react';
import { X } from 'tabler-icons-react';
import { BusinessTypes } from '../../../../enums/business-type';
import { MerchantOnboardingStatus } from '../../../../enums/merchant-onboarding-status';
import { OpenComponent } from '../../../../enums/misc.enum';
import { parseLink } from '../../../../helpers/view-information.helper';
import useAxiosPrivate from '../../../../hooks/useAxiosPrivate';
import useDidMountEffect from '../../../../hooks/useDidMountEffect';
import {
  IPaginationMeta,
  OnboardingInformationByTab,
} from '../../../../type/admin.types';
import { ErrorResponse } from '../../../../type/error-response.types';
import { BasicAccount } from '../../../../type/retrieve-account.types';
import MerchantInfoBusinessInfo from '../../../admin-merchant-info/components/merchant-info-business-info.component';
import MerchantDocuments from '../../../admin-merchant-info/components/merchant-info-documents.component';
import MerchantInfoGeneral from '../../../admin-merchant-info/components/merchant-info-general.component';
import MerchantInfoIncorporatorsSignatories from '../../../admin-merchant-info/components/merchant-info-incorporators-signatories.component';
import SkeletonMerchantInfoGeneral from '../../../admin-merchant-info/skeleton-loading/skeleton-merchant-info-general.component';
import AdminUserInformation from '../../../admin-user-management/admin-user-information.component';
import UserAccountsTable from '../../../tables/user-accounts-table.component';
import XPagination from '../../../x-components/x-pagination.component';
import MerchantDiscountRate from '../../../admin-merchant-info/components/merchant-info-mdr.component';
import { Entity } from '../../../../enums/entity.enums';

interface Props {
  loadAccountsData: () => Promise<void>;
  onboardingData: OnboardingInformationByTab | undefined;

  isLoading: boolean;
  paginationData: IPaginationMeta | undefined;
  isAccountsLoading: boolean;
  userData: BasicAccount[] | undefined;
  paginationRequest: ((page: number, limit: number) => void) | undefined;
  disabledReason?: string;
  isEntityActive?: boolean;
  parentTypeId?: number;
}
function EntityInfo(props: Readonly<Props>) {
  const axiosPrivate = useAxiosPrivate();
  const {
    loadAccountsData,
    onboardingData,
    isLoading,
    paginationData,
    isAccountsLoading,
    userData,
    paginationRequest,
    disabledReason,
    isEntityActive,
    parentTypeId,
  } = props;
  const [isRetrievingFiles, setIsRetrievingFiles] = useState(false);
  const [isChecked, setIsChecked] = useState<number[]>([]);
  const [checkedAll, setCheckedAll] = useState<boolean>(false);
  const [componentManager, setComponentManager] = useState(OpenComponent.HOME);
  const [clickedUserId, setClickedUserId] = useState<number>();

  useDidMountEffect(() => {
    loadAccountsData();
    setCheckedAll(false);
    setIsChecked([]);
  }, []);

  const downloadFiles = async () => {
    setIsRetrievingFiles(true);

    const endpoint = `/web/onboard/${onboardingData!.accountId}/files`;

    try {
      const response = await axiosPrivate.get(endpoint, {
        responseType: 'blob',
      });
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `${
          onboardingData!.general.merchantName || onboardingData!.accountId
        } files.zip`
      );
      link.click();
    } catch (err: any) {
      // @ref https://stackoverflow.com/a/67985097
      let responseObj = await err.response.data.text();

      const error: ErrorResponse = JSON.parse(responseObj);

      showNotification({
        id: 'failed-to-retrieve-merchant-files',
        message: error.error || 'An Unexpected Error Occurred',
        icon: <X size={16} />,
        color: 'red',
        autoClose: 10000,
      });
    } finally {
      setIsRetrievingFiles(false);
    }
  };

  const isSaveUnderBranch = parentTypeId === Entity.BRANCH;

  const getIncorporatorSignatoriesTabLabel = () => {
    if (!isLoading && onboardingData) {
      if (onboardingData.general.businessTypeId === BusinessTypes.STARTER)
        return 'Signatories';
      if (
        onboardingData.general.businessTypeId === BusinessTypes.SOLE_PROPRIETOR
      )
        return 'Proprietor & Signatories';
      return 'Incorporators & Signatories';
    } else {
      return 'Loading...';
    }
  };

  return (
    <>
      <Space h="sm" />
      {isLoading ? (
        <Stack></Stack>
      ) : (
        <>
          {disabledReason && !isEntityActive && (
            <Alert title="Disabled Reason" color="red" mb={22}>
              <Spoiler
                maxHeight={110}
                showLabel={<Text size="sm">Show more</Text>}
                hideLabel={<Text size="sm">Hide</Text>}
              >
                {disabledReason}
              </Spoiler>
            </Alert>
          )}
          {(onboardingData?.general.onboardingStatus ===
            MerchantOnboardingStatus.DECLINED ||
            onboardingData?.general.onboardingStatus ===
              MerchantOnboardingStatus.PENDING) && (
            <Alert
              data-cy="declinedReason"
              title="Declined Reason"
              color="red"
              mb={22}
            >
              <Spoiler
                maxHeight={110}
                showLabel={<Text size="sm">Show more</Text>}
                hideLabel={<Text size="sm">Hide</Text>}
              >
                {onboardingData.declinedReason}
              </Spoiler>
            </Alert>
          )}
        </>
      )}
      <Tabs>
        <Tabs.Tab
          label={'General Information'}
          data-cy="generalInfoTab"
          disabled={isLoading}
        >
          {!isLoading ? (
            <MerchantInfoGeneral
              generalData={onboardingData?.general}
              parseLink={parseLink}
            />
          ) : (
            <SkeletonMerchantInfoGeneral />
          )}
        </Tabs.Tab>
        {!isLoading &&
          onboardingData?.signatories &&
          (onboardingData?.signatories.length > 0 ||
            (onboardingData?.signatories.length > 0 &&
              onboardingData?.incorporators &&
              onboardingData?.incorporators.length > 0)) && (
            <Tabs.Tab
              data-cy="incorporatorSignatoriesTab"
              label={getIncorporatorSignatoriesTabLabel()}
              disabled={isLoading || !onboardingData}
            >
              <MerchantInfoIncorporatorsSignatories
                signatoriesData={onboardingData.signatories}
                incorporatorsData={onboardingData.incorporators}
                businessTypeId={onboardingData.general.businessTypeId}
              />
            </Tabs.Tab>
          )}

        <Tabs.Tab
          label={'Business Information'}
          data-cy="businessInfoTab"
          disabled={isLoading || !onboardingData}
        >
          {!isLoading && onboardingData ? (
            <MerchantInfoBusinessInfo
              businessData={onboardingData.businessInformation}
            />
          ) : (
            <></>
          )}
        </Tabs.Tab>
        <Tabs.Tab label={'Documents'} disabled={isLoading || !onboardingData}>
          {!isLoading && onboardingData ? (
            <MerchantDocuments
              downloadFiles={downloadFiles}
              isRetrievingFiles={isRetrievingFiles}
              files={onboardingData.documents}
              businessTypeId={onboardingData.general.businessTypeId}
              merchantId={onboardingData.accountId}
            />
          ) : (
            <></>
          )}
        </Tabs.Tab>
        <Tabs.Tab
          label={'User Accounts'}
          disabled={isAccountsLoading || !userData}
        >
          {userData && (
            <>
              {componentManager === OpenComponent.HOME ? (
                <>
                  <UserAccountsTable
                    accountsData={userData}
                    isChecked={isChecked}
                    setIsChecked={setIsChecked}
                    setCheckedAll={setCheckedAll}
                    checkedAll={checkedAll}
                    isLoading={isAccountsLoading}
                    setComponentManager={setComponentManager}
                    setClickedUserId={setClickedUserId}
                  />
                  {paginationData && paginationData.totalPages ? (
                    <XPagination
                      total={paginationData.totalPages}
                      page={paginationData.currentPage}
                      paginationRequest={paginationRequest}
                    />
                  ) : (
                    <></>
                  )}
                </>
              ) : (
                clickedUserId && (
                  <AdminUserInformation
                    selectedId={clickedUserId}
                    setComponentManager={setComponentManager}
                  />
                )
              )}
            </>
          )}
        </Tabs.Tab>
        {isSaveUnderBranch && (
          <Tabs.Tab label={'MDR'} disabled={isLoading}>
            <MerchantDiscountRate mdrFees={onboardingData?.mdrFees} />
          </Tabs.Tab>
        )}
      </Tabs>
    </>
  );
}

export default EntityInfo;
